import React, {
    useContext, useCallback, useEffect, useState
} from 'react';
import _ from 'lodash';
import { useModal } from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { ViewModelServiceContext, ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { MockUpUtil, EntityUtil } from '@xengage/gw-portals-util-js';

import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import config from 'app-config';
import { useUpdateSubmissionServices, validationUtil } from 'premier-capability-hooks';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import RVDriverComponent from '../../components/RVDriverComponent/RVDriverComponent';
import { PremierConstants } from 'premier-constant-service';
import presentationMetadata from './DriversPage.metadata.json5';

import messages from './DriversPage.messages';
import styles from './DriversPage.module.scss';
import { BreakpointTrackerContext } from '@jutro/legacy/layout';

function DriversPage(props) {
    const { wizardData: submissionVM, updateWizardData, currentStep,steps} = props;
    const { showConfirm, showAlert } = useModal();
    const viewModelService = useContext(ViewModelServiceContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const translator = useTranslator();
    const [showErrors, setShowErrors] = useState(false);
    const [isPageInitialized, setPageInitialized] = useState(false);
    const [isValidFlag, setIsValidFlag] = useState(true);
    useDependencies('LoadSaveService');

    const [showAccountHolderToggle, updateShowAccountHolderToggle] = useState(false);
    const [existingDrivers, setExistingDrivers] = useState([]);
    const [accountHolderDriver, setAccountHolderDriver] = useState({});
    const [driverPath, setDriverPath] = useState('');
    const jobNumber = submissionVM.value?.quoteID || submissionVM.value?.jobID;
    const { lineName } = PremierConstants;
    const {
        pageKey
    } = currentStep;
    const metadata = presentationMetadata[pageKey];
    const { updateSubmission, saveAndQuoteSubmissionService } = useUpdateSubmissionServices();

    useAuthentication();

    const {
        initialValidation,
        onValidate,
        isComponentValid,
        disregardFieldValidation
    } = useValidation('DriverPage');
	
	useEffect(() => {
        const vehicles = submissionVM.lobData.recVehLine.coverables && submissionVM.lobData.recVehLine.coverables.vehLocRisks ? submissionVM.lobData.recVehLine.coverables.vehLocRisks.children : [];

        if (vehicles.length > 0) {
            _.each(vehicles, (vehicle) => {
                vehicle.isValidationRequired.value = false;
            });
        }
       
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const createDriverVM = useCallback(() => {
        if (_.isEmpty(submissionVM.lobData.recVehLine.exposures.prvdriExposures.value)) {
            const accountHolder = submissionVM.baseData.accountHolder.value;
            let driverObj = {isValidationRequired: true};

            if (submissionVM.baseData.accountHolder.value.subtype === "Person") {
                updateShowAccountHolderToggle(true);
                driverObj = {
                    lastName: accountHolder.firstName,
                    firstName: accountHolder.lastName,
                    dateOfBirth: accountHolder.dateOfBirth,
                    gender_Ext: accountHolder.gender,
                    maritalStatus: accountHolder.maritalStatus,
                    isPolicyHolder: true,
                    person: accountHolder,
                    isValidationRequired: true,
                    sameAsAccountHolder: true,     
                    certificateDetails : [],
                    violations: []
                };
            } else {
                updateShowAccountHolderToggle(false);
                driverObj = {
                    isPolicyHolder: false,
                    sameAsAccountHolder: false, 
                    person: {},
                    isValidationRequired: true,
                    certificateDetails : [],
                    violations: []
                };
            }

            const { _dtoName, _xCenter } = submissionVM.lobData.recVehLine.exposures.prvdriExposures;
            const driverVM = viewModelService.create(driverObj, _xCenter, _dtoName);

            driverVM.offering_Ext = submissionVM.lobData.recVehLine.selectedOffering_Ext.value;
            submissionVM.lobData.recVehLine.exposures.prvdriExposures.pushElement(driverVM);
            updateWizardData(submissionVM);
        }
    }, [submissionVM, updateWizardData, viewModelService]);

    useEffect(() => {
        const driversPath = 'lobData.recVehLine.exposures.prvdriExposures';

        if (
            MockUpUtil.isValueMocked(
                submissionVM.value,
                'quote.recVehProduct',
                `${driversPath}.0.licenseNumber`
            )
        ) {
            const driverPath = `${driversPath}.value[0]`;
            const driver = _.get(submissionVM, driverPath);

            const newDriver = {
                ...driver,
                licenseNumber: undefined,
                gender: undefined
            };

            _.set(submissionVM, driverPath, newDriver);
            updateWizardData(submissionVM);
        }
       

        let drivers = _.get(submissionVM, `${driversPath}.value`);

        drivers = _.orderBy(drivers, ['isPolicyHolder'], ['desc']);
        _.each(drivers, (driver) => {
            driver.isValidationRequired = true;
        });
        

        const _existingDrivers = submissionVM.lobData.recVehLine.exposures.unassignedDrivers.value.map((c) => {
            c.id = c.publicID ;

            return c;
        });

        const accountHolder = submissionVM.baseData.accountHolder.value;

        const accHolderDriver = _existingDrivers.filter(eDriver => eDriver.publicID === accountHolder.publicID)

        setAccountHolderDriver(accHolderDriver)

        let updatedExistingDrivers = _existingDrivers

        if (_.isEmpty(drivers)) {
            createDriverVM();

            const accountHolder = submissionVM.baseData.accountHolder.value;

            updatedExistingDrivers = _existingDrivers.filter(eDriver => eDriver.publicID !== accountHolder.publicID)
        }

        const availableDrivers = [{ id: "placeholder", displayName: "Create New Driver" }].concat(updatedExistingDrivers);

        setExistingDrivers(availableDrivers);
        setPageInitialized(true);
        
        // No array dependencies needed in this hook.
        // The logic of initializing drivers data needs to be executed only once
        // when landing into drivers page.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submissionVM , setExistingDrivers, updateShowAccountHolderToggle, setAccountHolderDriver]);

    const onNext = useCallback(async () => {
        _.unset(submissionVM.value, 'bindData');

        const { currentStepIndex } = props;
        const newSteps = steps.slice(currentStepIndex + 1, steps.length);
        const driversPath = 'lobData.recVehLine.exposures.prvdriExposures.value';
        const driversList = _.get(submissionVM, driversPath);

        driversList.forEach((driver, index) => {
            if (
                _.isEmpty(driver.person.displayName)
                || !driver.person.displayName.includes(
                    `${driver.person.firstName} ${driver.person.lastName}`
                )
            ) {
                const displayname = `${driver.person.firstName} ${driver.person.lastName}`;

                _.set(submissionVM, `${driversPath}[${index}].person.displayName`, displayname);
            }

            if (_.isEmpty(driver.firstName) ||
            _.isEmpty(driver.lastName) ||
            _.isEmpty(driver.gender) ||
            _.isEmpty(driver.dateOfBirth)
        ) {
            _.set(submissionVM, `${driversPath}[${index}].firstName`, driver.person.firstName);
            _.set(submissionVM, `${driversPath}[${index}].lastName`, driver.person.lastName);
            _.set(submissionVM, `${driversPath}[${index}].gender_Ext`, driver.person.gender);
        //     _.set(submissionVM, `${driversPath}[${index}].dateOfBirth`, driver.person.dateOfBirth);
        }

        if (_.isEmpty(driver.dateOfBirth)) {
            _.set(submissionVM, `${driversPath}[${index}].dateOfBirth`, driver.person.dateOfBirth);
        }
        });

        const {jobType} = submissionVM.value.baseData;

        if(submissionVM.value.baseData.jobType === 'PolicyChange' && (newSteps.length === 1) ){
            const response = await saveAndQuoteSubmissionService('PRVRecVehLine', 'VehiclesPage', jobNumber, submissionVM.value, jobType, true);

            submissionVM.value = response.quoteResponse;
        } else{
            const response = await updateSubmission('PRVRecVehLine', 'VehiclesPage', submissionVM);

            submissionVM.value = response;
        }
        
        const allDrivers = submissionVM.lobData.recVehLine.exposures && submissionVM.lobData.recVehLine.exposures.prvdriExposures ? submissionVM.lobData.recVehLine.exposures.prvdriExposures.children : [];

        if (allDrivers.length > 0) {
            _.each(allDrivers, (driExp) => {
                driExp.isValidationRequired.value = true;
            });
        }
        
        updateWizardData(submissionVM);

        return submissionVM;
    }, [jobNumber, props, saveAndQuoteSubmissionService, steps, submissionVM, updateSubmission, updateWizardData]);

    const onSave = useCallback(
        async () => {
            

            if (submissionVM.lobData.recVehLine.exposures.prvdriExposures.value.length > 0
                && submissionVM.lobData.recVehLine.exposures.prvdriExposures.value.some(data => data.dateOfBirth === undefined)) {
                setShowErrors(true)

                showAlert({
                    title: 'Missing required field',
                    message: 'Date of Birth is required to save the data',
                    status: 'error',
                    icon: 'mi-error-outline'
                }).catch(_.noop);

                return false
            }

            const subVMCopy = _.clone(submissionVM)
            const productCode = _.get(subVMCopy.value, 'baseData.productCode');

            validationUtil.unSetIsValidationReqFlag(subVMCopy.lobData.recVehLine.exposures.prvdriExposures.value, ' ')

            const lobLineName = productCode ? lineName[productCode] : '';
            
            await updateSubmission(lobLineName, 'DriversPage', subVMCopy);
                            
            return submissionVM;
        },
        [lineName, submissionVM, updateSubmission]
    );

    const addDriver = useCallback(() => {
        const driverObj = {
            isPolicyHolder: false,
            tempID: EntityUtil.nextId(),
            isValidationRequired: true
        };
        const { _xCenter, _dtoName } = submissionVM.lobData.recVehLine.exposures.prvdriExposures;
        const driverVM = viewModelService.create(driverObj, _xCenter, _dtoName);

        driverVM.person = {};
        driverVM.certificateDetails = [];
        driverVM.violations = [];
        submissionVM.lobData.recVehLine.exposures.prvdriExposures.pushElement(driverVM);
        updateWizardData(submissionVM);
    }, [submissionVM, updateWizardData, viewModelService]);

    const removeDriver = useCallback(
        (evt) => {
            const driverListPath = 'lobData.recVehLine.exposures.prvdriExposures.value';
            const driverPath = evt.path;
            const driver = _.get(submissionVM, driverPath).value;
            const driverList = _.get(submissionVM, driverListPath);
            const driverIndex = driverList.findIndex(
                (driverFromList) => _.isEqual(driverFromList, driver)
            );

            showConfirm({
                title: messages.removeDriverTitle,
                message: messages.removeDriverDescription,
                status: 'warning',
                icon: 'mi-error-outline'
            }).then(
                (results) => {
                    if (results === 'cancel') {
                        return _.noop();
                    }

                    disregardFieldValidation(`driver${driverIndex}`);

                    const newDriverList = _.remove(driverList,
                        (driverFromList) => _.isEqual(driverFromList, driver));

                    _.set(submissionVM, newDriverList, driverListPath);
                    const newSubmissionVMClone = viewModelService.clone(submissionVM);
                     updateWizardData(newSubmissionVMClone);

                    if(driver.person.publicID != null){
                        driver.id = driver.person.publicID
                        driver.tempId = undefined
                        driver.displayName = driver.person.displayName

                        const updatedExistingDrivers =  existingDrivers.concat(driver)

                        setExistingDrivers(updatedExistingDrivers)
                    }
                    

                    return true;
                }, _.noop
            );
        },
        [submissionVM, showConfirm, disregardFieldValidation, updateWizardData, existingDrivers]
    );

    const addOrRemoveAccHolderinExistingDriver = useCallback((toRemove) => {
        let updatedExistingDrivers = existingDrivers;

        updatedExistingDrivers = toRemove ? updatedExistingDrivers.filter(eDriver => eDriver.publicID !== accountHolderDriver.publicID) : updatedExistingDrivers.concat(accountHolderDriver)
        setExistingDrivers(updatedExistingDrivers)

    },[existingDrivers, accountHolderDriver]);

    const generateOverrides = useCallback(() => {
        const drivers = _.get(submissionVM, 'lobData.recVehLine.exposures.prvdriExposures.value', []);
        const overrideProps = {};
        let readOnlyFields = ['driverFirstName', 'driverMiddleName', 'driverLastName', 'driverDateOfBirth'];

        if (config.persona === 'producer' || config.persona === 'csr') {
            readOnlyFields = ['driverFirstName', 'driverMiddleName', 'driverLastName'];
        }

        drivers.forEach((driver, index) => {
            const isAccountHolder = driver.isPolicyHolder && driver.sameAsAccountHolder;
            let driverTitle = `${translator(messages.rvDriverTitle)} ${index + 1}`
            
            if (drivers.length > 0 && !_.isUndefined(driver.person.firstName)&& !_.isUndefined(driver.person.lastName)) {
                driverTitle =  driver.displayName || `${driver.person.firstName} ${driver.person.lastName}` || `${translator(messages.rvDriverTitle)} ${index + 1}`;
            }

            overrideProps[`driver${index}`] = {
                readOnlyFields: isAccountHolder ? readOnlyFields : null,
                submissionVM,
                updateWizardData,
                isFromNewSubmission: true,
                showAccountHolderToggle,
                onValidate,
                setIsValidFlag,
                addOrRemoveAccHolderinExistingDriver
            };
            overrideProps[`driverDetailsAccordionCard${index}`] = {
                title: driverTitle
            };
            overrideProps[`rvDeleteDriver${index}`] = {
                visible: drivers.length > 1
            };
        });

        return overrideProps;
    }, [submissionVM, translator, updateWizardData, showAccountHolderToggle, addOrRemoveAccHolderinExistingDriver]);

    
    const setDefaultNewEntry = useCallback((driver) => {
        let driverObj = {
            isPolicyHolder: false,
            tempID: EntityUtil.nextId(),
            isValidationRequired: true,
            sameAsAccountHolder: false
        };

        if (driver) {
            driverObj = Object.assign(driverObj, driver)
        }

        const { _xCenter, _dtoName } = submissionVM.lobData.recVehLine.exposures.prvdriExposures;
        const driverVM = viewModelService.create(driverObj, _xCenter, _dtoName);

        driverVM.person = driver ? driverVM.person : {};
        driverVM.certificateDetails = [];
        driverVM.violations = [];
        driverVM.offering_Ext = submissionVM.lobData.recVehLine.selectedOffering_Ext.value;
        submissionVM.lobData.recVehLine?.exposures.prvdriExposures?.pushElement(driverVM);
    }, [submissionVM.lobData.recVehLine?.exposures?.prvdriExposures, submissionVM.lobData.recVehLine?.selectedOffering_Ext?.value, viewModelService]);


    const onAddNew = useCallback((driver) => {
        setDefaultNewEntry(driver);

        const newVehicleIndex = submissionVM.lobData.recVehLine.exposures.prvdriExposures.length - 1;

        setDriverPath(`lobData.recVehLine.exposures.prvdriExposures.children.${newVehicleIndex}`);
        updateWizardData(submissionVM);
    }, [setDefaultNewEntry, submissionVM, updateWizardData]);

    const addExistingDriver = (value) => {                
        if (value === 'placeholder') {
            onAddNew();

            return
        }

        const driver = submissionVM.lobData.recVehLine.exposures.unassignedDrivers.value.filter((c) => c.publicID == value)[0];

        if(driver){
            const _driver = {
                sameAsAccountHolder: false, 
                licenseNumber: driver.licenseNumber,
                licenseState: driver.licenseState,
                person: {
                    displayName: driver.displayName,
                    firstName: driver.firstName,
                    lastName: driver.lastName,
                    dateOfBirth: driver.dateOfBirth,
                    gender_Ext: driver.gender,
                    maritalStatus: driver.maritalStatus,
                    primaryPhoneType: driver.primaryPhoneType || undefined,
                    workNumber: driver.workNumber || undefined,
                    homeNumber: driver.homeNumber || undefined,
                    publicID: driver.publicID,
                    isPolicyHolder: driver.isPolicyHolder || false
                }
            }
    
            onAddNew(_driver);

            const updatedExistingDrivers = existingDrivers.filter(eDriver => eDriver.publicID !== driver.publicID)

            setExistingDrivers(updatedExistingDrivers)
        }
        
    }

    const overrideProps = {
        '@field': {
            // apply to all fields
            showOptional: false,
            showRequired: true,
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top'
        },
        addDriver: {
            disabled: !isComponentValid
        },
        driverContacts: {             
            availableValues: existingDrivers,
            onValueChange: addExistingDriver,
            searchable: true
        },
        ...generateOverrides()
    };

    const readValue = useCallback(
        (id, path) => readViewModelValue(metadata, submissionVM, id, path, overrideProps),
        [metadata, overrideProps, submissionVM]
    );

    const resolvers = {
        resolveCallbackMap: {
            onAddDriverClick: addDriver,
            onRemoveDriver: removeDriver,
            onValidate
        },
        resolveComponentMap: {
            rvdriverComponent: RVDriverComponent
        },
        resolveClassNameMap: styles,
    };

    if (!isPageInitialized) {
        return null;
    }

    const isDisableNext = () => _.get(submissionVM, 'lobData.recVehLine.exposures.prvdriExposures').value.length > 0 ? !_.get(submissionVM, 'lobData.recVehLine.exposures.prvdriExposures').aspects.subtreeValid : false;

    return (
        <WizardPage
         onNext={onNext}
         onSave={onSave}
         skipWhen={initialValidation}
         disableNext={isDisableNext()}
         >
            <ViewModelForm
                uiProps={metadata}
                model={submissionVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                resolveValue={readValue}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
            />
        </WizardPage>
    );
}

DriversPage.propTypes = wizardProps;
export default DriversPage;
